import { inject } from '@angular/core';
import { Router, CanActivateFn, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { from, of } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { DemoModeService } from '../services/demo-mode.service';
import { SystemAccessService } from '../services/system-access.service';
import { MembershipService } from '../services/membership.service';
import { publicReadableInPublicMode } from './route-data';
import { resolveStanding } from './standing.util';
import { AppConfigService } from '../services/app-config.service';
import { visitorBounce } from './visitor-bounce';

/**
 * Auth + membership guard for protected routes (/home, etc.).
 * - Unauthenticated → redirect to portal (store URL for post-auth redirect)
 * - Authenticated + member → allow through
 * - Authenticated + paused member → the access-paused wall (an honest explanation
 *   beats a silent bounce to the portal wearing no context)
 * - Authenticated + non-member → redirect to portal
 */
export const authGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const demoModeService = inject(DemoModeService);

  // Demo mode: API assigns user identity, no OIDC auth needed
  if (demoModeService.isDemo()) {
    return of(true);
  }

  const oidcSecurityService = inject(OidcSecurityService);
  const systemAccessService = inject(SystemAccessService);
  const membershipService = inject(MembershipService);
  const router = inject(Router);
  const appConfig = inject(AppConfigService);

  // A node the public may enter, met in public mode (the brand admits the public and this caller is not
  // admitted): the caller passes. ONE arm at TWO seats — here for an anonymous visitor, and below, after the
  // awaited read, for a signed-in non-member or a pending applicant: public mode is false until the
  // membership settles, so a single early seat would bounce them off a public page on a cold load. Every
  // other node answers exactly as before, public mode included — a public-mode caller never reaches a
  // member-only node's guard (the member area's own match keeps them out).
  const admittedInPublicMode = () => publicReadableInPublicMode(route, membershipService);

  return oidcSecurityService.isAuthenticated$.pipe(
    take(1),
    switchMap(({ isAuthenticated }) => {
      if (!isAuthenticated) {
        if (admittedInPublicMode()) return of(true);
        // Store the attempted URL for redirecting after auth.
        sessionStorage.setItem('authRedirectUrl', state.url);
        // The visitor's bounce: the portal, or straight to the paused page while the public brand is paused.
        return of(visitorBounce(router, appConfig, state.url));
      }

      // Authenticated — check membership. Platform-root monitors are admitted by NAME:
      // isMember() happens to read true for them today (the server reports their privilege
      // as Member), but that is an accident of representation — the explicit root leg keeps
      // this admission correct through any future privilege-level cleanup.
      return from(systemAccessService.getPrivilegeLevel()).pipe(
        switchMap(async () => {
          if (systemAccessService.isMember() || systemAccessService.isRoot()) {
            return true;
          }
          // No working privilege — a PAUSED member (membership row exists, deactivated)
          // lands on the wall that explains their state, never a silent portal bounce.
          const standing = await resolveStanding(membershipService);
          if (standing === 'paused' && !systemAccessService.isRoot()) {
            return router.createUrlTree(['/access-paused']);
          }
          // The settled seat: a signed-in non-member or a pending applicant on a public node passes.
          if (admittedInPublicMode()) return true;
          // Authenticated but not a member — the visitor's bounce (the portal, or the paused page)
          return visitorBounce(router, appConfig, state.url);
        })
      );
    })
  );
};
